import { SINGLE_PRICE, LABEL_REVIEW_PRICE, FULL_LABEL_REVIEW_PRICE } from "./prices.js.erb";

export class PaymentManager {
  constructor() {
    this.paymentElementComplete = false;
    this.submitButton = document.querySelector("#payment-submit");

    this.setPrice();
    this.mountElements();
    this.initPriceWatch();
    this.bindNoteField();
  }

  setPrice(price) {
    if (price !== undefined) {
      this.amount = price;
    } else {
      // SetupIntent (payment method change) doesn't use an amount
      const amountEl = document.querySelector("meta[name='payment-amount']");
      this.amount = amountEl ? Number(amountEl.content) : null;
    }

    // Push the amount into Elements so wallet UIs reflect the correct total
    // when annual/monthly is toggled via subscriptions/new.js
    if (this.elements && this.amount !== null) {
      this.elements.update({ amount: this.amount });
    }
  }

  mountElements() {
    const stripeKey = document.querySelector("meta[name='stripe-key']").content;
    this.stripe = window.Stripe(stripeKey);

    const fontsSrc = document.querySelector("#fonts-only-css-src").href;
    const style = {
      variables: {
        fontFamily:
          "'Lato', BlinkMacSystemFont, -apple-system, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', 'Helvetica', 'Arial', sans-serif;",
      },
      rules: {
        ".Label": {
          fontSize: "1rem",
          fontWeight: "700",
          color: "#3f4a56",
        },
      },
    };

    const elementsOptions = {
      currency: "usd",
      mode: document.querySelector("meta[name='payment-mode']").content,
      amount: this.amount,

      fonts: [{ cssSrc: fontsSrc }],
      appearance: style,
    };
    const setupFutureUsage = document.querySelector("meta[name='setup-future-usage']")?.content;
    if (setupFutureUsage) {
      elementsOptions.setupFutureUsage = setupFutureUsage;
    }

    const pmcId = document.querySelector("meta[name='payment-method-configuration']")?.content;
    if (pmcId) {
      elementsOptions.paymentMethodConfiguration = pmcId;
    }

    this.elements = this.stripe.elements(elementsOptions);

    const paymentElementOptions = { layout: "tabs" };
    const paymentElement = this.elements.create("payment", paymentElementOptions);
    this.paymentElement = paymentElement;
    paymentElement.mount("#payment-element");
    paymentElement.on("change", (event) => this.onPaymentElementChange(event));

    document
      .querySelector("#payment-element")
      .closest("form")
      .addEventListener("submit", (event) => this.onSubmit(event));
  }

  onPaymentElementChange(event) {
    // Switching payment types can yield an 'empty', but still 'complete' event
    // so we must check both
    this.paymentElementComplete = event.complete && !event.empty;

    if (this.paymentElementComplete) {
      // Stripe will display validation errors
      this.elements.submit();
    }

    this.updateSubmitButton();
  }

  async onSubmit(event) {
    const form = event.target;
    // Prevent form submission, we send with fetch
    event.preventDefault();

    try {
      // https://docs.stripe.com/js/elements/submit
      // Validates form fields and collects data for link/google/apple pay wallets
      const { error: submitError } = await this.elements.submit();
      if (submitError) return;

      const submitButton = form.querySelector("[type='submit']");
      if (submitButton.disabled) {
        return;
      }

      submitButton.disabled = true;
      submitButton.classList.add("is-loading");

      // Send form to our server to create a Stripe PaymentIntent/SetupIntent
      const formData = new FormData(form);
      const response = await fetch(form.action, {
        method: form.method,
        body: formData,
      });

      if (!response.ok) {
        if (!response.headers.get("content-type").includes("application/json")) {
          throw new Error("Server error. Contact us or try again later");
        }

        const result = await response.json();
        throw new Error(result.message);
      }

      const result = await response.json();

      // Confirm PaymentIntent/SetupIntent with Stripe servers
      // https://docs.stripe.com/js/payment_intents/confirm_payment
      // https://docs.stripe.com/js/setup_intents/confirm_setup
      const confirmIntent =
        result.type == "setup" ? this.stripe.confirmSetup : this.stripe.confirmPayment;
      const { error } = await confirmIntent({
        elements: this.elements,
        clientSecret: result.clientSecret,
        confirmParams: {
          return_url: result.return_url,
        },
      });

      if (error) {
        throw new Error(error.message);
      }
    } catch (error) {
      const errorField = form.querySelector("#payment-errors");
      errorField.textContent = error.message;

      const submitButton = form.querySelector("[type='submit']");
      submitButton.classList.remove("is-loading");
      submitButton.disabled = false;
    }
  }

  updateSubmitButton() {
    if (this.paymentElementComplete) {
      this.submitButton.disabled = false;
    } else {
      this.submitButton.disabled = true;
    }
  }

  initPriceWatch() {
    const totalEl = document.querySelector("#purchase_total");
    if (!totalEl) {
      return;
    }

    const updatePurchaseTotal = (amount) => {
      if (amount <= 0) {
        return;
      }

      this.amount = amount * 100;
      totalEl.textContent = amount;
      this.elements.update({ amount: this.amount });
    };

    // charges/new
    document.querySelector("#charge_quantity")?.addEventListener("input", (event) => {
      updatePurchaseTotal((event.target.valueAsNumber || 0) * SINGLE_PRICE);
    });

    // payments/new
    document.querySelector("#payment_amount")?.addEventListener("input", (event) => {
      updatePurchaseTotal(event.target.valueAsNumber || 0);
    });

    // label_reviews/new
    document.querySelectorAll("#label_review_next_day, #label_review_quantity").forEach((el) => {
      el.addEventListener("input", (event) => {
        let total;
        if (document.querySelector("#label_review_full_review").value == "true") {
          total = FULL_LABEL_REVIEW_PRICE;
        } else {
          total = LABEL_REVIEW_PRICE;
        }

        total = total * (document.querySelector("#label_review_quantity").valueAsNumber || 0);

        document.querySelector("#next-day-price").textContent = total;
        if (document.querySelector("#label_review_next_day").checked) {
          total *= 2;
        }

        updatePurchaseTotal(total);
      });
    });
  }

  bindNoteField() {
    // Adjusts pluralization on Label Review form
    const containerElem = document.querySelector("#label-review-notes");
    if (!containerElem) {
      return;
    }

    const noteInputElem = containerElem.querySelector("input");
    const quantityField = document.querySelector("#label_review_quantity");
    const noteLabelElem = document.querySelector("label[for='label_review_notes']");
    if (quantityField && noteLabelElem) {
      quantityField.addEventListener("input", () => {
        const quantity = quantityField.valueAsNumber || 0;
        const currentText = noteLabelElem.textContent;
        const match = currentText.match(/Which (\w+) /);
        if (!match) return;

        const word = match[1];
        if (quantity && quantity > 1) {
          const plural = word.endsWith("s") ? word : word + "s";
          noteLabelElem.textContent = currentText.replace(/Which \w+ /, `Which ${plural} `);
          noteInputElem.placeholder = noteInputElem.placeholder.replace(
            /Which \w+ /,
            `Which ${plural} `
          );
        } else {
          const singular = word.endsWith("s") ? word.slice(0, -1) : word;
          noteLabelElem.textContent = currentText.replace(/Which \w+ /, `Which ${singular} `);
          noteInputElem.placeholder = noteInputElem.placeholder.replace(
            /Which \w+ /,
            `Which ${singular} `
          );
        }
      });
    }
  }
}

const initialize = () => {
  const stripeKey = document.querySelector("meta[name='stripe-key']")?.content;
  if (!stripeKey) {
    return;
  }

  window.payment_manager = new PaymentManager();
};

document.addEventListener("DOMContentLoaded", initialize);
